import { CATALOG_DISABLED_EXCHANGES } from '@/lib/configs/exchange-registry'
import { Exchange } from '@/types/generated/common/types'

/**
 * Exchanges whose instrument catalogs are shown in account-agnostic instrument
 * pickers (watchlist wizard, tab-add/edit pickers). Not filtered by which
 * accounts the user has connected — the catalog itself is account-independent.
 */
export const SUPPORTED_EXCHANGES: { exchange: Exchange; label: string }[] = [
  { exchange: Exchange.EXCHANGE_DERIBIT, label: 'Deribit' },
  { exchange: Exchange.EXCHANGE_BYBIT, label: 'Bybit' },
  { exchange: Exchange.EXCHANGE_HYPERLIQUID, label: 'Hyperliquid' },
  { exchange: Exchange.EXCHANGE_DERIVE, label: 'Derive' },
  { exchange: Exchange.EXCHANGE_UPBIT, label: 'Upbit' },
  { exchange: Exchange.EXCHANGE_BINANCE, label: 'Binance' },
  { exchange: Exchange.EXCHANGE_LIGHTER, label: 'Lighter' },
  { exchange: Exchange.EXCHANGE_PAPERTRADE, label: 'Papertrade' },
  // Korean equities, reached through the Toss venue. Labelled by the market
  // (KRX) rather than the broker, matching `getExchangeLabel`.
  { exchange: Exchange.EXCHANGE_XKRX, label: 'KRX' },
  // Databento-sourced market-data-only venues — no account-creation venue exists
  // for these (exchangeToTradeVenue always returns undefined for them), so they're
  // viewable/chartable but never tradeable. CME futures (GLBX), US equities/ETFs
  // (XNAS/XNYS, e.g. IBIT), options (OPRA). See resolveExchangeTradeability in
  // lib/utils/account-select.ts, which every "add to Order Form" entry point
  // checks before accepting an instrument from one of these.
  { exchange: Exchange.EXCHANGE_GLBX, label: 'GLBX' },
  { exchange: Exchange.EXCHANGE_XNAS, label: 'XNAS' },
  { exchange: Exchange.EXCHANGE_XNYS, label: 'XNYS' },
  { exchange: Exchange.EXCHANGE_OPRA, label: 'OPRA' },
]

/** Display label per SUPPORTED_EXCHANGES entry; `undefined` for any other exchange. */
export const SUPPORTED_EXCHANGE_LABELS: ReadonlyMap<Exchange, string> = new Map(
  SUPPORTED_EXCHANGES.map(({ exchange, label }) => [exchange, label])
)

export const SUPPORTED_EXCHANGE_LIST: Exchange[] = SUPPORTED_EXCHANGES.map(
  ({ exchange }) => exchange
)

/**
 * Subset of SUPPORTED_EXCHANGE_LIST whose instrument catalog should actually
 * be fetched — excludes CATALOG_DISABLED_EXCHANGES (lib/configs/exchange-registry.ts),
 * a static, manual override independent of GetVenues' per-venue `enabled`
 * (service/venue.hook.ts's isExchangeEnabled). Pass this, not
 * SUPPORTED_EXCHANGE_LIST, to `useEnabledExchangeList`/`useMasterInstrumentLists`
 * call sites that issue the catalog fetch itself — a disabled exchange stays
 * fully listed everywhere else (the wizard's exchange picker, SUPPORTED_EXCHANGE_LABELS
 * lookups), it just never issues an instrumentlist/master-instrument request.
 */
export const CATALOG_FETCHABLE_EXCHANGE_LIST: Exchange[] = SUPPORTED_EXCHANGE_LIST.filter(
  (exchange) => !CATALOG_DISABLED_EXCHANGES.has(exchange)
)
